import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layer, Line, Stage, Text, Circle, Group } from "react-konva";
import { StickyNote } from "../setting/StickyNote";
import useUserMode from "../store/useUserMode";

const ST_SELECT = "select";
const ST_RECTANGLE = "callout";
const ST_LINE = "line";
const ST_VERTICAL_LINE = "vertical";
const ST_HORIZONTAL_LINE = "horizontal";
const ST_FIBONACCI = "fibonacci";
const ST_UNDO = "undo";
const ST_CLEAR = "clear";

function useStateCallback(initialState) {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null);

  const setStateCallback = useCallback((state, cb) => {
    cbRef.current = cb;
    setState(state);
  }, []);

  useEffect(() => {
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null;
    }
  }, [state]);

  return [state, setStateCallback];
}

const AnnotationStage = ({ setting, height, width, data, style, xScale: xScaleProp, yScale: yScaleProp, xAccessor, scaleUpdateCounter, chartScalesRef, hideZoomControls = false, onEndpointsOverControlsChange, isMobileView = false, controlMarginBottom = 24 }) => {
  const stageRef = useRef(null);
  const isDrawing = useRef(false);
  const [isDraggable, setIsDraggable] = useState(false);
  // shapes store data coordinates: { type, dataPoints: [timestamp1, price1, timestamp2, price2] }
  // Using timestamps instead of bar indices so trendlines persist across data changes
  const [shapes, setShapes] = useStateCallback([]);
  const history = useRef([]);
  const [fibonacciLines, setFibonacciLines] = useState([]);
  const userMode = useUserMode((state) => state.userMode);
  const setUserMode = useUserMode((state) => state.setUserMode);
  const [texts, setTexts] = useState({});
  const [swidth, setSwidth] = useState(200);
  const [sheight, setSheight] = useState(200);
  const [selected, setSelected] = useState(false);
  const [disableScroll, setDisableScroll] = useState(false);
  // Track which shape is being hovered for showing endpoint circles
  const [hoveredShapeIndex, setHoveredShapeIndex] = useState(null);

  // Track if any trendline endpoint is overlapping the control button area
  const [endpointsOverControls, setEndpointsOverControls] = useState(false);

  // For whole line dragging: store initial drag info per shape index.
  const groupDragInfo = useRef({});

  // Helper: Calculate slope of a line from data points (ensures slope preservation during drag)
  const calculateSlope = useCallback((dataPoints) => {
    if (!dataPoints || dataPoints.length < 4) return null;
    const [t1, p1, t2, p2] = dataPoints;
    if (t1 === t2) return Infinity; // Vertical line
    return (p2 - p1) / (t2 - t1);
  }, []);

  // Helper: Clip line to chart boundaries while maintaining slope
  // Returns clipped pixel points that stay within chart bounds
  const clipLineToChart = useCallback((points, chartWidth, chartHeight) => {
    let [x1, y1, x2, y2] = points;

    // If line is fully within bounds, return as-is
    const isWithin = x1 >= 0 && x1 <= chartWidth && x2 >= 0 && x2 <= chartWidth &&
                     y1 >= 0 && y1 <= chartHeight && y2 >= 0 && y2 <= chartHeight;
    if (isWithin) return points;

    // Calculate line equation: y = mx + b
    const dx = x2 - x1;
    const dy = y2 - y1;

    if (Math.abs(dx) < 0.0001) {
      // Nearly vertical line - just clip Y
      return [x1, Math.max(0, Math.min(chartHeight, y1)), x2, Math.max(0, Math.min(chartHeight, y2))];
    }

    const m = dy / dx;
    const b = y1 - m * x1;

    // Helper to get y from x
    const getY = (x) => m * x + b;
    // Helper to get x from y
    const getX = (y) => (y - b) / m;

    // Clip start point
    let cx1 = x1, cy1 = y1;
    let cx2 = x2, cy2 = y2;

    // Clip against left edge (x = 0)
    if (cx1 < 0) {
      cx1 = 0;
      cy1 = getY(0);
    }
    if (cx2 < 0) {
      cx2 = 0;
      cy2 = getY(0);
    }

    // Clip against right edge (x = chartWidth)
    if (cx1 > chartWidth) {
      cx1 = chartWidth;
      cy1 = getY(chartWidth);
    }
    if (cx2 > chartWidth) {
      cx2 = chartWidth;
      cy2 = getY(chartWidth);
    }

    // Clip against top edge (y = 0)
    if (cy1 < 0) {
      cy1 = 0;
      cx1 = getX(0);
    }
    if (cy2 < 0) {
      cy2 = 0;
      cx2 = getX(0);
    }

    // Clip against bottom edge (y = chartHeight)
    if (cy1 > chartHeight) {
      cy1 = chartHeight;
      cx1 = getX(chartHeight);
    }
    if (cy2 > chartHeight) {
      cy2 = chartHeight;
      cx2 = getX(chartHeight);
    }

    return [cx1, cy1, cx2, cy2];
  }, []);

  // Helper: Shorten line so its visible ends stop at the outer edge of the
  // endpoint circles instead of passing through them. Only the ends that still
  // sit on the original (un-clipped) endpoint are trimmed — clipped ends have
  // no circle on them, so they keep their chart-edge position.
  const shortenLineForCircles = useCallback((clippedPoints, originalPoints, radius) => {
    const [cx1, cy1, cx2, cy2] = clippedPoints;
    const dx = cx2 - cx1;
    const dy = cy2 - cy1;
    const len = Math.hypot(dx, dy);
    if (len <= radius * 2) return clippedPoints;
    const ux = dx / len;
    const uy = dy / len;
    const startMatches =
      Math.abs(cx1 - originalPoints[0]) < 0.5 && Math.abs(cy1 - originalPoints[1]) < 0.5;
    const endMatches =
      Math.abs(cx2 - originalPoints[2]) < 0.5 && Math.abs(cy2 - originalPoints[3]) < 0.5;
    return [
      startMatches ? cx1 + ux * radius : cx1,
      startMatches ? cy1 + uy * radius : cy1,
      endMatches ? cx2 - ux * radius : cx2,
      endMatches ? cy2 - uy * radius : cy2,
    ];
  }, []);

  // Use scales from ref for real-time access during scroll/zoom, fallback to props
  // This ensures trendlines update immediately without waiting for React's render cycle
  const xScale = chartScalesRef?.current?.xScale ?? xScaleProp;
  const yScale = chartScalesRef?.current?.yScale ?? yScaleProp;

  // ---------------------------------------------------------------------------
  // Continuous bar-index <-> timestamp mapping.
  //
  // Client: "trendlines ... move on their own after you release them."
  //
  // Both directions used to SNAP to a whole bar: pixelToTimestamp did
  // Math.round(barIndex) before reading that bar's date, and this function
  // returned the nearest bar's index. A trendline endpoint dropped between two
  // bars therefore stored a timestamp that was not where the pointer was, and
  // converting it back put the endpoint on the bar centre — the line visibly
  // jumped as soon as anything recomputed it.
  //
  // The pair below is a true inverse. An endpoint between bar 40 and bar 41 is
  // stored as the time 40.37 of the way between those two bars' timestamps, and
  // converting back yields index 40.37 and the pixel it was dropped on. Nothing
  // is rounded, so release is a no-op and pan / zoom / a panel opening move the
  // line by exactly the amount the chart moved.
  //
  // Interpolation happens INSIDE a bracket of two real adjacent bars, never
  // across the whole array, so weekends and market gaps (which make bars evenly
  // spaced in index but not in time) are handled correctly.
  // ---------------------------------------------------------------------------

  // Average ms between adjacent bars — only used to extrapolate beyond the ends.
  const msPerBar = useCallback(() => {
    if (!data || data.length < 2) return null;
    const first = new Date(data[0].date).getTime();
    const last = new Date(data[data.length - 1].date).getTime();
    const firstIdx = xAccessor ? xAccessor(data[0]) : 0;
    const lastIdx = xAccessor ? xAccessor(data[data.length - 1]) : data.length - 1;
    const span = lastIdx - firstIdx;
    if (!span) return null;
    return (last - first) / span;
  }, [data, xAccessor]);

  // Fractional bar index -> timestamp.
  const timeAtBarIndex = useCallback((barIndex) => {
    if (!data || data.length === 0 || !Number.isFinite(barIndex)) return null;
    const baseIdx = xAccessor ? xAccessor(data[0]) : 0;
    const pos = barIndex - baseIdx; // float position into the data array
    const n = data.length;
    const perBar = msPerBar();

    if (pos <= 0) {
      const t0 = new Date(data[0].date).getTime();
      return perBar === null ? t0 : t0 + pos * perBar;
    }
    if (pos >= n - 1) {
      const tN = new Date(data[n - 1].date).getTime();
      return perBar === null ? tN : tN + (pos - (n - 1)) * perBar;
    }
    const lo = Math.floor(pos);
    const frac = pos - lo;
    const tLo = new Date(data[lo].date).getTime();
    const tHi = new Date(data[lo + 1].date).getTime();
    return tLo + (tHi - tLo) * frac;
  }, [data, xAccessor, msPerBar]);

  // Timestamp -> fractional bar index. Exact inverse of timeAtBarIndex.
  const findBarIndexFromTimestamp = useCallback((timestamp) => {
    if (!data || data.length === 0 || timestamp === null || timestamp === undefined) return null;

    const targetTime = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
    if (!Number.isFinite(targetTime)) return null;

    const n = data.length;
    const baseIdx = xAccessor ? xAccessor(data[0]) : 0;
    const perBar = msPerBar();
    const timeAt = (i) => new Date(data[i].date).getTime();

    if (targetTime <= timeAt(0)) {
      if (perBar === null) return baseIdx;
      return baseIdx + (targetTime - timeAt(0)) / perBar;
    }
    if (targetTime >= timeAt(n - 1)) {
      const lastIdx = xAccessor ? xAccessor(data[n - 1]) : n - 1;
      if (perBar === null) return lastIdx;
      return lastIdx + (targetTime - timeAt(n - 1)) / perBar;
    }

    // Largest j with time(j) <= targetTime.
    let lo = 0;
    let hi = n - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (timeAt(mid) <= targetTime) lo = mid; else hi = mid;
    }
    const tLo = timeAt(lo);
    const tHi = timeAt(lo + 1);
    const span = tHi - tLo;
    const frac = span > 0 ? (targetTime - tLo) / span : 0;
    const idxLo = xAccessor ? xAccessor(data[lo]) : lo;
    return idxLo + frac;
  }, [data, xAccessor, msPerBar]);

  // Pixel X -> timestamp, WITHOUT rounding to a bar (see the note above).
  const pixelToTimestamp = useCallback((pixelX) => {
    if (!xScale || !data || data.length === 0) return null;
    const barIndex = xScale.invert(pixelX);
    if (!Number.isFinite(barIndex)) return null;
    return timeAtBarIndex(barIndex);
  }, [xScale, data, timeAtBarIndex, scaleUpdateCounter]);

  // Helper functions to convert between pixel and data coordinates
  const pixelToData = useCallback((pixelX, pixelY) => {
    if (!xScale || !yScale) return { timestamp: null, price: null, isValid: false };

    const timestamp = pixelToTimestamp(pixelX);
    const price = yScale.invert(pixelY);

    // Verify the conversion produced valid numbers
    if (timestamp === null || !Number.isFinite(price)) {
      return { timestamp: null, price: null, isValid: false };
    }
    return { timestamp, price, isValid: true };
  }, [xScale, yScale, pixelToTimestamp, scaleUpdateCounter]);

  // Clamp a pointer position to the valid range of the chart scales so that
  // pixelToData can still produce valid data coordinates when the user starts
  // a trendline right at the edge of the plot (e.g. near the bottom border).
  // Without this, edge clicks land outside xScale/yScale ranges, invert()
  // returns non-finite values, and the shape is created with dataPoints=null
  // which then can't survive a scale update.
  const clampToScaleRange = useCallback((x, y) => {
    let cx = x;
    let cy = y;
    if (xScale && typeof xScale.range === 'function') {
      const r = xScale.range();
      if (r && r.length >= 2) {
        const lo = Math.min(r[0], r[1]);
        const hi = Math.max(r[0], r[1]);
        cx = Math.max(lo, Math.min(hi, x));
      }
    }
    if (yScale && typeof yScale.range === 'function') {
      const r = yScale.range();
      if (r && r.length >= 2) {
        const lo = Math.min(r[0], r[1]);
        const hi = Math.max(r[0], r[1]);
        cy = Math.max(lo, Math.min(hi, y));
      }
    }
    return { x: cx, y: cy };
  }, [xScale, yScale]);

  const dataToPixel = useCallback((timestamp, price) => {
    if (!xScale || !yScale) return { x: null, y: null };

    // Find the bar index for this timestamp
    const barIndex = findBarIndexFromTimestamp(timestamp);

    let x = null;
    if (barIndex !== null) {
      x = xScale(barIndex);
    }

    // If x is still invalid, extrapolate based on timestamp
    if (!Number.isFinite(x) && data && data.length >= 2) {
      const domain = xScale.domain();
      const range = xScale.range();

      if (domain && range && domain.length >= 2 && range.length >= 2) {
        const firstBar = data.find(d => xAccessor(d) === domain[0]) || data[0];
        const lastBar = data.find(d => xAccessor(d) === domain[1]) || data[data.length - 1];

        if (firstBar && lastBar) {
          const firstTime = new Date(firstBar.date).getTime();
          const lastTime = new Date(lastBar.date).getTime();
          const targetTime = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();

          // Linear interpolation based on time
          const timeRatio = (targetTime - firstTime) / (lastTime - firstTime);
          x = range[0] + timeRatio * (range[1] - range[0]);
        }
      }
    }

    let y = yScale(price);

    // If y is invalid, try to extrapolate
    if (!Number.isFinite(y)) {
      const domain = yScale.domain();
      const range = yScale.range();
      if (domain && range && domain.length >= 2 && range.length >= 2) {
        const domainSpan = domain[1] - domain[0];
        const rangeSpan = range[1] - range[0];
        if (domainSpan !== 0) {
          const pixelsPerUnit = rangeSpan / domainSpan;
          y = range[0] + (price - domain[0]) * pixelsPerUnit;
        }
      }
    }

    return {
      x: Number.isFinite(x) ? x : null,
      y: Number.isFinite(y) ? y : null
    };
  }, [xScale, yScale, data, xAccessor, findBarIndexFromTimestamp, scaleUpdateCounter]);

  // THE single place a shape's pixel points are derived from its data points.
  //
  // Both callers below use it — the scale-change effect and handleMouseUp /
  // handleTouchEnd — so a freshly drawn line and a recomputed one can never be
  // placed by two slightly different rules. That divergence is what made a line
  // jump on an unrelated click (see the note in handleMouseUp).
  //
  // Returns the shape unchanged when it has no data coordinates or when the
  // conversion cannot produce finite pixels; callers can compare by identity.
  const settleShapeToData = useCallback((shape) => {
    if (!shape || !shape.dataPoints) return shape;
    const [t1, p1, t2, p2] = shape.dataPoints;
    let newPoints = null;

    if (shape.type === ST_VERTICAL_LINE && t1 !== null) {
      const px = dataToPixel(t1, 0);
      if (px.x !== null) newPoints = [px.x, 0, px.x, height || window.innerHeight];
    } else if (shape.type === ST_HORIZONTAL_LINE && p1 !== null) {
      const px = dataToPixel(0, p1);
      if (px.y !== null) newPoints = [0, px.y, width || window.innerWidth, px.y];
    } else if (t1 !== null && p1 !== null && t2 !== null && p2 !== null) {
      const start = dataToPixel(t1, p1);
      const end = dataToPixel(t2, p2);
      if (start.x !== null && start.y !== null && end.x !== null && end.y !== null) {
        newPoints = [start.x, start.y, end.x, end.y];
      }
    }

    return newPoints ? { ...shape, points: newPoints } : shape;
  }, [dataToPixel, height, width]);

  // Recompute pixel positions from data coordinates whenever scales settle
  // (pan end / zoom / a panel being added or removed above or below the price
  // chart). This keeps trendlines anchored to their timestamp/price.
  useEffect(() => {
    if (!xScale || !yScale || !data || data.length === 0) return;

    let needsUpdate = false;
    const updatedShapes = shapes.map(shape => {
      const settled = settleShapeToData(shape);
      if (settled !== shape) needsUpdate = true;
      return settled;
    });

    if (needsUpdate) {
      setShapes(updatedShapes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleUpdateCounter]);

  // Return stored pixel points for rendering — updated by the effect above
  const getPixelPoints = useCallback((shape) => {
    return shape.points || [0, 0, 0, 0];
  }, []);

  // Helper: Check if a point (x, y) is within the control button area
  // Control buttons render only on mobile when hideZoomControls is false
  // (see Chart.js — `{isMobileView && !hideZoomControls && (<div>…buttons…</div>)}`).
  // When no buttons are rendered, there is no area to protect.
  const isPointInControlArea = useCallback((x, y) => {
    if (!isMobileView || hideZoomControls) return false;

    // Buttons are 34px tall, 3 wide with 10px gaps (~122px), anchored at
    // `bottom: controlMarginBottom + 6`. Use a tight footprint so we don't
    // bleed into the plot area above the buttons.
    const controlAreaHeight = 46;  // 34 button + small touch padding
    const controlAreaWidth = 140;  // 3*34 + 2*10 gaps + small padding
    const bottomGap = controlMarginBottom + 6;

    const controlLeft = (width / 2) - (controlAreaWidth / 2);
    const controlRight = (width / 2) + (controlAreaWidth / 2);
    const controlBottom = height - bottomGap + controlAreaHeight / 2;
    const controlTop = controlBottom - controlAreaHeight;

    return x >= controlLeft && x <= controlRight && y >= controlTop && y <= controlBottom;
  }, [width, height, isMobileView, hideZoomControls, controlMarginBottom]);

  // Helper: Check if a click is near any existing trendline endpoint.
  // Used to decide whether a click in LINE mode should start a NEW trendline
  // (click far from all endpoints) or edit an existing endpoint (near a circle).
  // Tap-zone is intentionally larger than the visible 6px circle so touch works.
  const EDIT_HIT_RADIUS = 20;       // tap-zone around trendline endpoint circles
  const LINE_HIT_RADIUS = 10;       // tap-zone around a vertical/horizontal line body
  const isNearAnyEndpoint = useCallback((x, y) => {
    for (const shape of shapes) {
      if (!shape.isComplete) continue;
      const pts = shape.points;
      if (!pts || pts.length < 4) continue;

      if (shape.type === ST_LINE) {
        // Trendline: check both endpoint circles.
        if (Math.hypot(x - pts[0], y - pts[1]) <= EDIT_HIT_RADIUS) return true;
        if (Math.hypot(x - pts[2], y - pts[3]) <= EDIT_HIT_RADIUS) return true;
      } else if (shape.type === ST_VERTICAL_LINE) {
        // Vertical line spans full height at x = pts[0]. Block new-line
        // creation if the click is on the line body so the drag handler runs.
        if (Math.abs(x - pts[0]) <= LINE_HIT_RADIUS) return true;
      } else if (shape.type === ST_HORIZONTAL_LINE) {
        // Horizontal line spans full width at y = pts[1].
        if (Math.abs(y - pts[1]) <= LINE_HIT_RADIUS) return true;
      }
    }
    return false;
  }, [shapes]);

  // Helper: Check if any trendline endpoint is over the control button area
  const checkEndpointsOverControls = useCallback(() => {
    if (hideZoomControls || shapes.length === 0) return false;

    for (const shape of shapes) {
      if (!shape.points || shape.points.length < 4) continue;
      if (shape.type !== ST_LINE && shape.type !== ST_VERTICAL_LINE && shape.type !== ST_HORIZONTAL_LINE) continue;

      const [x1, y1, x2, y2] = shape.points;

      // Check both endpoints
      if (isPointInControlArea(x1, y1) || isPointInControlArea(x2, y2)) {
        return true;
      }
    }
    return false;
  }, [shapes, hideZoomControls, isPointInControlArea]);

  // Update endpointsOverControls whenever shapes change
  useEffect(() => {
    const newValue = checkEndpointsOverControls();
    setEndpointsOverControls(newValue);
    if (onEndpointsOverControlsChange) {
      onEndpointsOverControlsChange(newValue);
    }
  }, [shapes, checkEndpointsOverControls, scaleUpdateCounter, onEndpointsOverControlsChange]);

  // Effect to compute dataPoints for shapes that don't have them yet
  // This handles the case where trendlines were drawn before scales were ready
  useEffect(() => {
    // Skip if scales aren't ready or no data
    if (!xScale || !yScale || !data || data.length === 0) return;

    // Check if any shapes need dataPoints computed
    let needsUpdate = false;
    const updatedShapes = shapes.map(shape => {
      // Only process line types that need dataPoints
      if (shape.dataPoints) return shape; // Already has dataPoints
      if (!shape.points || shape.points.length < 4) return shape;
      if (shape.type !== ST_LINE && shape.type !== ST_VERTICAL_LINE && shape.type !== ST_HORIZONTAL_LINE) {
        return shape;
      }

      // Try to compute dataPoints (timestamps) from current pixel positions
      const startData = pixelToData(shape.points[0], shape.points[1]);
      const endData = pixelToData(shape.points[2], shape.points[3]);

      // Check if conversion produced valid results
      if (!startData.isValid || !endData.isValid) {
        return shape; // Conversion failed
      }

      needsUpdate = true;
      let dataPoints;
      if (shape.type === ST_VERTICAL_LINE) {
        dataPoints = [startData.timestamp, null, endData.timestamp, null];
      } else if (shape.type === ST_HORIZONTAL_LINE) {
        dataPoints = [null, startData.price, null, endData.price];
      } else {
        dataPoints = [startData.timestamp, startData.price, endData.timestamp, endData.price];
      }

      return { ...shape, dataPoints };
    });

    if (needsUpdate) {
      setShapes(updatedShapes);
    }
  }, [xScale, yScale, data, shapes, setShapes, pixelToData]);

  useEffect(() => {
    const handleTouchMove = (event) => {
      if (disableScroll) {
        event.preventDefault();
      }
    };

    if (disableScroll) {
      document.body.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
    } else {
      document.body.removeEventListener("touchmove", handleTouchMove);
    }

    return () => {
      document.body.removeEventListener("touchmove", handleTouchMove);
    };
  }, [disableScroll]);

// useEffect(() => {
//   const handleDuplicateTrendline = () => {
//     // Find last trendline
//     const lastTrendline = [...shapes].reverse().find(shape => shape.type === ST_LINE);
//     if (lastTrendline) {
//       const offset = 10; // small shift so it’s visible
//       const newPoints = [
//         lastTrendline.points[0] + offset,
//         lastTrendline.points[1] + offset,
//         lastTrendline.points[2] + offset,
//         lastTrendline.points[3] + offset,
//       ];
//       const newShape = { ...lastTrendline, points: newPoints };
//       const updatedShapes = [...shapes, newShape];
//       setShapes(updatedShapes);
//       history.current = [...history.current, updatedShapes];
//     }
//   };

//   window.addEventListener("duplicateTrendline", handleDuplicateTrendline);
//   return () => {
//     window.removeEventListener("duplicateTrendline", handleDuplicateTrendline);
//   };
// }, [shapes]);

const shapesRef = useRef(shapes);
useEffect(() => {
  shapesRef.current = shapes;
}, [shapes]);

useEffect(() => {
  const handleDuplicateTrendline = () => {
    const lastTrendline = [...shapesRef.current]
      .reverse()
      .find(
        shape =>
          shape.type === ST_LINE &&
          !(shape.points[0] === shape.points[2] && shape.points[1] === shape.points[3]) // ignore zero-length
      );

    console.log("Duplicating trendline:", lastTrendline);

    if (lastTrendline) {
      // Calculate the line direction
      const origPixelDx = lastTrendline.points[2] - lastTrendline.points[0];
      const origPixelDy = lastTrendline.points[3] - lastTrendline.points[1];
      const origPixelLength = Math.sqrt(origPixelDx * origPixelDx + origPixelDy * origPixelDy);

      // Unit direction vector
      const dirX = origPixelLength > 0 ? origPixelDx / origPixelLength : 1;
      const dirY = origPixelLength > 0 ? origPixelDy / origPixelLength : 0;

      // Perpendicular direction (rotated 90 degrees) - this is where we offset
      // Using (dy, -dx) to offset "below" or "to the right" of the line
      const perpX = dirY;
      const perpY = -dirX;

      // If perpY is negative (line going up-right), flip to offset below
      // We want the duplicate to appear below/right of the original
      const offsetDistance = 30; // pixel offset for visual separation
      const offsetX = perpX * offsetDistance;
      const offsetY = perpY * offsetDistance;

      // If offset would place line above, flip it
      const finalOffsetX = offsetY < 0 ? -offsetX : offsetX;
      const finalOffsetY = offsetY < 0 ? -offsetY : offsetY;

      const newPoints = [
        lastTrendline.points[0] + finalOffsetX,
        lastTrendline.points[1] + finalOffsetY,
        lastTrendline.points[2] + finalOffsetX,
        lastTrendline.points[3] + finalOffsetY,
      ];

      // Store the original pixel-space direction for exact slope preservation during rendering
      const pixelDirection = {
        dx: dirX,
        dy: dirY,
        length: origPixelLength,
      };

      // For duplicate dataPoints, we need to calculate based on the new pixel center position
      // This ensures the duplicate line is positioned correctly
      let newDataPoints = null;
      let originalSlope = null;
      let lineVector = null;

      if (lastTrendline.dataPoints && lastTrendline.dataPoints.length >= 4) {
        // Store the exact line vector (difference between endpoints) - this never changes
        lineVector = {
          dt: lastTrendline.dataPoints[2] - lastTrendline.dataPoints[0], // time difference
          dp: lastTrendline.dataPoints[3] - lastTrendline.dataPoints[1], // price difference
        };

        // Calculate slope
        if (lastTrendline.dataPoints[2] !== lastTrendline.dataPoints[0]) {
          originalSlope = (lastTrendline.dataPoints[3] - lastTrendline.dataPoints[1]) /
                         (lastTrendline.dataPoints[2] - lastTrendline.dataPoints[0]);
        }

        // Calculate new dataPoints by applying the same offset proportionally
        // Use a consistent offset in data space based on the line's characteristics
        const barOffset = 3; // offset by 3 bars worth of time
        const priceOffset = Math.abs(lastTrendline.dataPoints[1] - lastTrendline.dataPoints[3]) * 0.1 || 10;

        newDataPoints = [
          lastTrendline.dataPoints[0] + barOffset,
          lastTrendline.dataPoints[1] + priceOffset,
          lastTrendline.dataPoints[2] + barOffset,
          lastTrendline.dataPoints[3] + priceOffset,
        ];
      }
      // Mark as duplicated and store original slope + line vector for parallel drag constraint
      const newShape = {
        ...lastTrendline,
        points: newPoints,
        dataPoints: newDataPoints,
        isDuplicate: true,
        isComplete: true, // Duplicates are always complete
        originalSlope: originalSlope,
        lineVector: lineVector, // Exact line vector for preserving slope during drag
        pixelDirection: pixelDirection, // Store pixel-space direction for exact rendering
      };
      const updatedShapes = [...shapesRef.current, newShape];
      setShapes(updatedShapes);
      history.current = [...history.current, updatedShapes];
    }
  };

  window.addEventListener("duplicateTrendline", handleDuplicateTrendline);
  return () => {
    window.removeEventListener("duplicateTrendline", handleDuplicateTrendline);
  };
}, []); // 👈 no [shapes] dependency


  useEffect(() => {
    if (history.current.length > 0) {
      history.current.pop();
      setShapes(history.current[history.current.length - 1] || []);
    }
  }, []);


useEffect(() => {
  isDrawing.current = false;  // Cancel active drawing when switching mode
  // your switch code here...
}, [userMode]);


  useEffect(() => {
    if (isDrawing.current) {
  isDrawing.current = false;
  setShapes(prev => prev.slice(0, -1));  // remove last
}


    switch (userMode) {
      case ST_SELECT:
        setIsDraggable(true);
        break;
      case ST_LINE:
        setIsDraggable(false);
        break;
      case ST_RECTANGLE:
        setIsDraggable(false);
        break;
      case ST_FIBONACCI: {
        const yMaxValues = data.map((d) => d.bb.top);
        const yMinValues = data.map((d) => d.bb.bottom);
        const yMaxAxisValues = data.map((d) => d.bb.top);
        const yMinAxisValues = data.map((d) => d.bb.bottom);

        let maxPoint = Math.max(...yMaxValues);
        let minPoint = Math.min(...yMinValues);

        const yMaxAxis = Math.max(...yMaxAxisValues);
        const yMinAxis = Math.min(...yMinAxisValues);
        maxPoint =
          (window.innerHeight * (maxPoint - yMinAxis)) / (yMaxAxis - yMinAxis);
        minPoint =
          (window.innerHeight * (minPoint - yMinAxis)) / (yMaxAxis - yMinAxis);

        const diff = maxPoint - minPoint;

        setFibonacciLines([
          { pos: minPoint, ratio: "0.0" },
          { pos: minPoint + diff * 0.236, ratio: "23.6" },
          { pos: minPoint + diff * 0.382, ratio: "38.2" },
          { pos: minPoint + diff * 0.5, ratio: "50.0" },
          { pos: minPoint + diff * 0.618, ratio: "61.8" },
          { pos: maxPoint, ratio: "100.0" },
        ]);

        const newShapes = [
          ...shapes,
          { type: ST_FIBONACCI, points: [fibonacciLines] },
        ];

        setShapes(newShapes);
        history.current = [...history.current, newShapes];
        break;
      }
      case ST_UNDO:
        if (history.current.length > 0) {
          history.current.pop();
          setShapes(history.current[history.current.length - 1] || []);
        }
        break;
      case ST_CLEAR:
        setShapes([]);
        break;
      default:
        break;
    }
  }, [userMode]);

  // Hide/show blue control-point circles when drawing menu is toggled
  useEffect(() => {
    if (!setting.drawMode) {
      setHoveredShapeIndex(null);
      setIsDraggable(false);
    } else {
      // Restore isDraggable based on current userMode when menu reopens
      setIsDraggable(userMode === ST_SELECT);
    }
  }, [setting.drawMode]);

  function resetSelection() {
    const myCustomEvent = new CustomEvent("resetSelection", {
      detail: { resetSelection: true },
    });
    window.dispatchEvent(myCustomEvent);
  }

  const handleMouseDown = (e) => {
    if (!setting.drawMode) return;

    // Get pointer position first
    const stage = e.target.getStage();
    const pos = stage ? stage.getPointerPosition() : null;

    // If we can't get position, return early
    if (!pos) return;

    // If clicking in control button area and no trendline endpoints are over it,
    // don't start drawing - let the click pass through to the buttons
    if (isPointInControlArea(pos.x, pos.y) && !endpointsOverControls) {
      return;
    }

    // If the click is near an existing endpoint circle, let Konva's drag on
    // that circle take over — don't start a new trendline. This is what lets
    // the user stay in LINE mode after drawing and still edit existing
    // endpoints by tapping near them.
    if (userMode !== ST_SELECT && isNearAnyEndpoint(pos.x, pos.y)) {
      return;
    }

    isDrawing.current = true;

  // in select mode, don't interfere — let Konva handle dragging
  if (userMode === ST_SELECT) return;

  isDrawing.current = true;

  // Convert pixel position to data coordinates for trendlines.
  // Clamp first so a click on the very edge of the plot still yields valid
  // dataPoints — keep the original pos for the visual line so it starts
  // exactly under the cursor.
  const clamped = clampToScaleRange(pos.x, pos.y);
  const dataCoords = pixelToData(clamped.x, clamped.y);

    switch (userMode) {
      case ST_RECTANGLE:
        setShapes(
          [...shapes, { type: ST_RECTANGLE, points: [pos.x, pos.y, 0, 0] }],
          () => {
            resetSelection();
          }
        );
        break;
      case ST_LINE:
        setDisableScroll(true);
        // Store both pixel points (for immediate display) and data points (timestamps for persistence)
        // Only store dataPoints if conversion was valid
        setShapes([...shapes, {
          type: ST_LINE,
          points: [pos.x, pos.y, pos.x, pos.y],
          dataPoints: dataCoords.isValid
            ? [dataCoords.timestamp, dataCoords.price, dataCoords.timestamp, dataCoords.price]
            : null // Will be computed when scales become available
        }]);
        break;
      case ST_VERTICAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          {
            type: ST_VERTICAL_LINE,
            points: [pos.x, 0, pos.x, window.innerHeight],
            dataPoints: dataCoords.isValid
              ? [dataCoords.timestamp, null, dataCoords.timestamp, null] // null price = full height
              : null
          },
        ]);
        break;
      case ST_HORIZONTAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          {
            type: ST_HORIZONTAL_LINE,
            points: [0, pos.y, window.innerWidth, pos.y],
            dataPoints: dataCoords.isValid
              ? [null, dataCoords.price, null, dataCoords.price] // null timestamp = full width
              : null
          },
        ]);
        break;
      default:
        break;
    }
  };

const handleMouseMove = (e) => {
  if (!isDraggable && shapes.length > 0 && isDrawing.current) {
    const pos = e.target.getStage().getPointerPosition();
    let newShape = { ...shapes[shapes.length - 1] };

    // 🛡️ Safety: Only update if the shape matches the current mode!
    if (newShape.type !== userMode) return;

    // Convert pixel position to data coordinates
    const dataCoords = pixelToData(pos.x, pos.y);

    switch (newShape.type) {
      case ST_RECTANGLE:
        newShape.points[2] = pos.x - newShape.points[0];
        newShape.points[3] = pos.y - newShape.points[1];
        break;
      case ST_LINE:
        setDisableScroll(true);
        newShape.points[2] = pos.x;
        newShape.points[3] = pos.y;
        // Update data coordinates for the end point (only if we have valid dataPoints)
        if (newShape.dataPoints && dataCoords.isValid) {
          newShape.dataPoints = [...newShape.dataPoints];
          newShape.dataPoints[2] = dataCoords.timestamp;
          newShape.dataPoints[3] = dataCoords.price;
        } else if (!newShape.dataPoints && dataCoords.isValid) {
          // If dataPoints was null but now scales are valid, create them
          const startDataCoords = pixelToData(newShape.points[0], newShape.points[1]);
          if (startDataCoords.isValid) {
            newShape.dataPoints = [startDataCoords.timestamp, startDataCoords.price, dataCoords.timestamp, dataCoords.price];
          }
        }
        break;
      default:
        break;
    }
    setShapes([...shapes.slice(0, shapes.length - 1), newShape]);
  }
};


  const handleMouseUp = () => {
    if (!isDraggable) {
      isDrawing.current = false;
      // Mark the last shape as complete so slope-preserving logic can be applied
      if (shapes.length > 0) {
        const lastShape = shapes[shapes.length - 1];
        if (lastShape.type === ST_LINE || lastShape.type === ST_VERTICAL_LINE || lastShape.type === ST_HORIZONTAL_LINE) {
          // For trendlines (ST_LINE), a click without a real drag is a missed
          // click and produces a stub shape — discard it. Vertical and
          // horizontal lines are single-click placements (no drag expected),
          // so this check does not apply to them.
          //
          // Client: "When you draw a trendline and then click on the screen, it
          // moves the trendline."
          //
          // Part of what they were seeing is stub lines. The threshold was 5px,
          // and Konva's own drag threshold is 3px, which leaves a 2px window
          // where a click registers as a drag but is not discarded. Measured on
          // the running chart: a click carrying 4px of pointer jitter travels
          // hypot(4,4) = 5.7px and survived as a visible stub next to the real
          // trendline. A deliberate trendline spans tens to hundreds of pixels,
          // so raising the floor to 12px cannot reject a line anyone meant to
          // draw, while a slipped click no longer leaves anything behind.
          if (lastShape.type === ST_LINE) {
            const [x1, y1, x2, y2] = lastShape.points || [0, 0, 0, 0];
            const MIN_DRAG_PX = 12;
            const dragDist = Math.hypot(x2 - x1, y2 - y1);
            if (dragDist < MIN_DRAG_PX) {
              // Missed click — discard the degenerate shape and stay in draw mode
              setShapes(shapes.slice(0, -1));
              setDisableScroll(false);
              return;
            }
          }

          // Settle the shape onto its DATA coordinates before it is stored.
          //
          // This is the other half of the same client report, and the half that
          // made the line appear to jump on an unrelated click.
          //
          // A shape carries two representations: `points` (pixels, written live
          // while dragging) and `dataPoints` (timestamp + price). They are not
          // the same thing — pixelToTimestamp ROUNDS to the nearest bar, so the
          // timestamp stored for a drag that ended between two bars is the bar's,
          // not the pointer's. The line went on being drawn from the raw pixels
          // until something bumped scaleUpdateCounter, at which point the
          // recompute effect re-derived it from dataPoints and it snapped to a
          // different place.
          //
          // Measured before this change, on a still click (zero pointer travel)
          // with the drawing menu CLOSED and the Konva stage at
          // pointer-events:none, so the click never reached the annotation layer
          // at all — and with xScale/yScale domain AND range identical before and
          // after, so the chart itself had not moved:
          //     drawn at [250, 150, 550, 380]
          //     after one click [250.5, 150, 509.4, 351]
          // A 40px jump with nothing on screen having changed. That is the
          // "click moves the trendline, even after you close the trendline menu"
          // in the report.
          //
          // Deriving the pixels from the data the moment the line is finished
          // makes the two agree from the start. Any snap to the bar grid now
          // happens once, on mouse-up, under the user's eye — the same magnet
          // behaviour other charting tools have — and every later recompute is a
          // no-op because it produces the points already on screen.
          const settled = settleShapeToData(lastShape);
          const completedShapes = [...shapes.slice(0, -1), { ...settled, isComplete: true }];
          setShapes(completedShapes);
          history.current = [...history.current, completedShapes];
          // Stay in LINE (draw) mode so the user can immediately draw another
          // trendline. Existing endpoints become editable via the proximity
          // check in handleMouseDown / handleTouchStart.
        } else {
          history.current = [...history.current, shapes];
        }
      } else {
        history.current = [...history.current, shapes];
      }
      setDisableScroll(false);
    }
  };

  const handleTouchStart = (e) => {
    if (!setting.drawMode) return;
    const touchPos = e.target.getStage().getPointerPosition();
    const pos = { x: touchPos.x, y: touchPos.y };

    // If the tap is near an existing endpoint circle, let the circle's drag
    // handler take it — don't start a new trendline.
    if (userMode !== ST_SELECT && isNearAnyEndpoint(pos.x, pos.y)) {
      return;
    }

    isDrawing.current = true;

    // Convert pixel position to data coordinates for trendlines.
    // Clamp to scale range so edge taps still produce valid dataPoints.
    const clamped = clampToScaleRange(pos.x, pos.y);
    const dataCoords = pixelToData(clamped.x, clamped.y);

    switch (userMode) {
      case ST_RECTANGLE:
        setShapes(
          [...shapes, { type: ST_RECTANGLE, points: [pos.x, pos.y, 0, 0] }],
          () => {
            resetSelection();
          }
        );
        break;
      case ST_LINE:
        setDisableScroll(true);
        setShapes([...shapes, {
          type: ST_LINE,
          points: [pos.x, pos.y, pos.x, pos.y],
          dataPoints: dataCoords.isValid
            ? [dataCoords.timestamp, dataCoords.price, dataCoords.timestamp, dataCoords.price]
            : null
        }]);
        break;
      case ST_VERTICAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          {
            type: ST_VERTICAL_LINE,
            points: [pos.x, 0, pos.x, window.innerHeight],
            dataPoints: dataCoords.isValid
              ? [dataCoords.timestamp, null, dataCoords.timestamp, null]
              : null
          },
        ]);
        break;
      case ST_HORIZONTAL_LINE:
        setDisableScroll(true);
        setShapes([
          ...shapes,
          {
            type: ST_HORIZONTAL_LINE,
            points: [0, pos.y, window.innerWidth, pos.y],
            dataPoints: dataCoords.isValid
              ? [null, dataCoords.price, null, dataCoords.price]
              : null
          },
        ]);
        break;
      default:
        break;
    }
  };

  const handleTouchMove = (e) => {
    if (isDrawing && shapes.length > 0 && isDrawing.current) {
      const touchPos = e.target.getStage().getPointerPosition();
      const pos = { x: touchPos.x, y: touchPos.y };
      let newShape = { ...shapes[shapes.length - 1] };
      newShape.points = newShape.points.slice();

      // Convert pixel position to data coordinates
      const dataCoords = pixelToData(pos.x, pos.y);

      switch (newShape.type) {
        case ST_RECTANGLE:
          newShape.points[2] = pos.x - newShape.points[0];
          newShape.points[3] = pos.y - newShape.points[1];
          break;
        case ST_LINE:
          setDisableScroll(true);
          newShape.points[2] = pos.x;
          newShape.points[3] = pos.y;
          // Update data coordinates for the end point (only if we have valid dataPoints)
          if (newShape.dataPoints && dataCoords.isValid) {
            newShape.dataPoints = [...newShape.dataPoints];
            newShape.dataPoints[2] = dataCoords.timestamp;
            newShape.dataPoints[3] = dataCoords.price;
          } else if (!newShape.dataPoints && dataCoords.isValid) {
            // If dataPoints was null but now scales are valid, create them
            const startDataCoords = pixelToData(newShape.points[0], newShape.points[1]);
            if (startDataCoords.isValid) {
              newShape.dataPoints = [startDataCoords.timestamp, startDataCoords.price, dataCoords.timestamp, dataCoords.price];
            }
          }
          break;
        default:
          break;
      }

      setShapes([...shapes.slice(0, shapes.length - 1), newShape]);
    }
  };

  const handleTouchEnd = () => {
    isDrawing.current = false;
    // Mark the last shape as complete so slope-preserving logic can be applied
    if (shapes.length > 0) {
      const lastShape = shapes[shapes.length - 1];
      if (lastShape.type === ST_LINE || lastShape.type === ST_VERTICAL_LINE || lastShape.type === ST_HORIZONTAL_LINE) {
        // Same two rules as the mouse path, so a phone and a desktop cannot
        // behave differently: a tap that never became a real drag leaves nothing
        // behind, and a finished line is settled onto its data coordinates
        // immediately so no later recompute can move it. The client asked for
        // both desktop and mobile to be checked.
        if (lastShape.type === ST_LINE) {
          const [tx1, ty1, tx2, ty2] = lastShape.points || [0, 0, 0, 0];
          if (Math.hypot(tx2 - tx1, ty2 - ty1) < 12) {
            setShapes(shapes.slice(0, -1));
            setDisableScroll(false);
            return;
          }
        }
        const settled = settleShapeToData(lastShape);
        const completedShapes = [...shapes.slice(0, -1), { ...settled, isComplete: true }];
        setShapes(completedShapes);
        history.current = [...history.current, completedShapes];
        // Auto-switch to select mode so user can immediately edit the trendline
        setUserMode(ST_SELECT);
      } else {
        history.current = [...history.current, shapes];
      }
    } else {
      history.current = [...history.current, shapes];
    }
    setDisableScroll(false);
  };

  const handleTextChange = (index, value) => {
    const updatedTexts = { ...texts };
    updatedTexts[index] = value;
    setTexts(updatedTexts);
  };

  // Track touch to distinguish tap (zoom) vs drag (trendline) on zoom button area
  const tapTrackRef = useRef(null);

  const handleStageTouchStart = useCallback((e) => {
    const stage = stageRef.current;
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    tapTrackRef.current = { x: pos.x, y: pos.y, time: Date.now(), moved: false };
  }, []);

  const handleStageTouchMove = useCallback(() => {
    if (tapTrackRef.current) {
      tapTrackRef.current.moved = true;
    }
  }, []);

  const handleStageTouchEnd = useCallback((e) => {
    const tap = tapTrackRef.current;
    tapTrackRef.current = null;
    if (!tap || tap.moved) return;
    // Only forward quick taps (< 300ms)
    const elapsed = Date.now() - tap.time;
    if (elapsed > 300) return;
    const stage = stageRef.current;
    if (!stage) return;

    // Check if tap is in control area
    const tapInControlArea = isPointInControlArea(tap.x, tap.y);

    // If tap is NOT in control area, don't forward (normal drawing behavior)
    if (!tapInControlArea) return;

    // If tap IS in control area but trendline endpoints are over controls, don't forward
    // (allow user to interact with trendline endpoint)
    if (endpointsOverControls) return;

    // Tap is in control area and NO endpoints are over it - forward click to button
    const container = stage.container();
    const rect = container.getBoundingClientRect();
    const clientX = rect.left + tap.x;
    const clientY = rect.top + tap.y;

    // Temporarily hide canvas to find elements underneath
    const prev = container.style.pointerEvents;
    container.style.pointerEvents = 'none';
    const el = document.elementFromPoint(clientX, clientY);
    container.style.pointerEvents = prev;

    if (el) {
      const btn = el.closest('.chart-zoom-btn');
      if (btn) {
        // Stop the current event from doing anything else
        e.cancelBubble = true;

        // Create and dispatch a proper click event on the button
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX: clientX,
          clientY: clientY
        });
        btn.dispatchEvent(clickEvent);
      }
    }
  }, [isPointInControlArea, endpointsOverControls]);

  return (
    <>
      <Stage
        style={style}
        width={width + 100}
        height={height + 50}
        ref={stageRef}
        listening={!!setting?.drawMode}
        onMouseDown={(e) => { handleStageTouchStart(e); handleMouseDown(e); }}
        onTouchStart={(e) => { handleStageTouchStart(e); handleMouseDown(e); }}
        onMouseMove={(e) => { handleStageTouchMove(); handleMouseMove(e); }}
        onTouchMove={(e) => { handleStageTouchMove(); handleMouseMove(e); }}
        onMouseUp={(e) => { handleStageTouchEnd(e); handleMouseUp(e); }}
        onTouchEnd={(e) => { handleStageTouchEnd(e); handleMouseUp(e); }}
      >
        <Layer>
          {shapes.map((shape, i) => {
            if (shape.type === ST_RECTANGLE) {
              return (
            <StickyNote
              key={i}
              x={shape.points[0]}
              y={shape.points[1]}
              text={texts[i] || ""}
              colour="#FFDAE1"
              onTextChange={(value) => handleTextChange(i, value)}
              width={220}
              height={50}
              selected={selected}
              draggable={userMode === ST_SELECT}  // <---- key point
              onTextResize={(newWidth, newHeight) => {
                setSwidth(newWidth);
                setSheight(newHeight);
              }}
              onClick={() => setSelected(!selected)}
              onTextClick={(newSelected) => setSelected(newSelected)}
            />

              );
            } else if (shape.type === ST_FIBONACCI) {
              return (
                <React.Fragment key={i}>
                  {fibonacciLines.map((line, idx) => (
                    <React.Fragment key={idx}>
                      <Line
                        points={[0, line.pos, window.innerWidth, line.pos]}
                        stroke="red"
                        strokeWidth={1}
                        draggable={isDraggable}
                      />
                      <Text text={`${line.ratio}%`} y={line.pos} fill="red" />
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            } else if (shape.type === ST_LINE) {
              // Get pixel points from data coordinates (enables scroll/zoom alignment)
              const pixelPoints = getPixelPoints(shape);
              // Clip line to chart boundaries while maintaining visual slope
              const clippedPoints = clipLineToChart(pixelPoints, width, height);
              // Completed shapes are editable in any mode (LINE or SELECT) so the
              // user can stay in draw mode and still grab endpoints to edit.
              const canEdit = !!shape.isComplete;
              // Show circles only while the drawing menu is open. Closing the
              // trendline option hides all endpoint affordances so the chart
              // stays clean. Within drawMode: visible when complete, hovered,
              // or in SELECT mode. Hide entirely when posting (hideZoomControls).
              const showCircles = !hideZoomControls && !!setting?.drawMode && (hoveredShapeIndex === i || isDraggable || canEdit);
              // When endpoint circles are visible, shorten the visual line so it
              // does not poke through the transparent circle interiors.
              const displayPoints = showCircles
                ? shortenLineForCircles(clippedPoints, pixelPoints, 6)
                : clippedPoints;
              return (
                <Group
                  key={i}
                  onMouseEnter={() => setHoveredShapeIndex(i)}
                  onMouseLeave={() => setHoveredShapeIndex(null)}
                >
                  {/* Visual line - use clipped points for display */}
                  <Line
                    name="visual-line"
                    points={displayPoints}
                    stroke="#004C50"
                    strokeWidth={1}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation={
                      isDraggable ? "source-over" : "destination-over"
                    }
                    // Line itself is not directly draggable.
                    draggable={false}
                  />
                  {/* Draggable transparent overlay to move the whole line */}
                  <Line
                    name="overlay-line"
                    points={clippedPoints}
                    stroke="black"
                    strokeWidth={15}
                    opacity={0}
                    draggable={canEdit}
                    onDragStart={(e) => {
                      // Store initial data coordinates, pixel position, and slope for parallel constraint
                      const slope = shape.dataPoints ? calculateSlope(shape.dataPoints) : null;
                      // Use stored lineVector from shape if it's a duplicate, otherwise calculate from current dataPoints
                      const lineVector = shape.lineVector || (shape.dataPoints ? {
                        dt: shape.dataPoints[2] - shape.dataPoints[0], // time difference
                        dp: shape.dataPoints[3] - shape.dataPoints[1], // price difference
                      } : null);

                      groupDragInfo.current[i] = {
                        initialDataPoints: shape.dataPoints ? shape.dataPoints.slice() : null,
                        initialPoints: pixelPoints.slice(),
                        initialPos: { x: e.target.x(), y: e.target.y() },
                        isDuplicate: shape.isDuplicate || false,
                        originalSlope: shape.originalSlope || slope,
                        // Store the original line vector (difference between endpoints) for exact preservation
                        lineVector: lineVector,
                      };
                    }}
                    onDragMove={(e) => {
                      const info = groupDragInfo.current[i];
                      if (info) {
                        const deltaX = e.target.x() - info.initialPos.x;
                        const deltaY = e.target.y() - info.initialPos.y;

                        // Calculate new pixel points (simple translation maintains slope)
                        const newPixelPoints = [
                          info.initialPoints[0] + deltaX,
                          info.initialPoints[1] + deltaY,
                          info.initialPoints[2] + deltaX,
                          info.initialPoints[3] + deltaY,
                        ];

                        let newDataPoints;

                        // For duplicate lines (or any line), preserve exact slope by using line vector
                        if (info.isDuplicate && info.initialDataPoints && info.lineVector) {
                          // Calculate offset from the center point of the line for better accuracy
                          const centerPixelX = (newPixelPoints[0] + newPixelPoints[2]) / 2;
                          const centerPixelY = (newPixelPoints[1] + newPixelPoints[3]) / 2;
                          const centerData = pixelToData(centerPixelX, centerPixelY);

                          if (centerData.isValid) {
                            // Reconstruct both endpoints from center using original line vector
                            const halfDt = info.lineVector.dt / 2;
                            const halfDp = info.lineVector.dp / 2;
                            newDataPoints = [
                              centerData.timestamp - halfDt,
                              centerData.price - halfDp,
                              centerData.timestamp + halfDt,
                              centerData.price + halfDp,
                            ];
                          } else {
                            // Fallback: calculate delta from start point only
                            const startData = pixelToData(newPixelPoints[0], newPixelPoints[1]);
                            if (startData.isValid && info.initialDataPoints) {
                              const deltaTime = startData.timestamp - info.initialDataPoints[0];
                              const deltaPrice = startData.price - info.initialDataPoints[1];
                              newDataPoints = [
                                info.initialDataPoints[0] + deltaTime,
                                info.initialDataPoints[1] + deltaPrice,
                                info.initialDataPoints[2] + deltaTime,
                                info.initialDataPoints[3] + deltaPrice,
                              ];
                            }
                          }
                        } else {
                          // For non-duplicate lines, convert both endpoints independently
                          const startData = pixelToData(newPixelPoints[0], newPixelPoints[1]);
                          const endData = pixelToData(newPixelPoints[2], newPixelPoints[3]);
                          newDataPoints = [startData.timestamp, startData.price, endData.timestamp, endData.price];
                        }

                        const newShapes = [...shapes];
                        newShapes[i] = {
                          ...newShapes[i],
                          points: newPixelPoints,
                          dataPoints: newDataPoints,
                          // Preserve duplicate metadata, lineVector, and pixelDirection
                          isDuplicate: info.isDuplicate,
                          originalSlope: info.originalSlope,
                          lineVector: info.lineVector, // Keep original line vector
                          pixelDirection: shape.pixelDirection, // Keep original pixel direction
                        };
                        setShapes(newShapes);
                      }
                    }}
                    onDragEnd={(e) => {
                      // Reset overlay's position after dragging.
                      e.target.position({ x: 0, y: 0 });
                    }}
                  />
                  {/* Start endpoint circle - visible on hover, hidden when posting */}
                  <Circle
                    x={pixelPoints[0]}
                    y={pixelPoints[1]}
                    radius={showCircles ? 6 : 3}
                    fill="transparent"
                    stroke={showCircles ? "#004C50" : "transparent"}
                    strokeWidth={showCircles ? 2.5 : 0}
                    draggable={canEdit}
                    opacity={showCircles ? 1 : 0}
                    onMouseEnter={(e) => {
                      e.target.getStage().container().style.cursor = 'grab';
                    }}
                    onMouseLeave={(e) => {
                      e.target.getStage().container().style.cursor = 'default';
                    }}
                    onDragStart={(e) => {
                      e.target.getStage().container().style.cursor = 'grabbing';
                    }}
                    onDragEnd={(e) => {
                      e.target.getStage().container().style.cursor = 'grab';
                    }}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      // Update lines imperatively for real-time visual feedback
                      const newPoints = [pos.x, pos.y, pixelPoints[2], pixelPoints[3]];
                      const group = e.target.parent;
                      const clipped = clipLineToChart(newPoints, width, height);
                      const displayClipped = shortenLineForCircles(clipped, newPoints, 6);
                      const visualLine = group.findOne('.visual-line');
                      const overlayLine = group.findOne('.overlay-line');
                      if (visualLine) visualLine.points(displayClipped);
                      if (overlayLine) overlayLine.points(clipped);

                      // Convert new pixel position to data coordinates (timestamp)
                      const newStartData = pixelToData(pos.x, pos.y);

                      const newShapes = [...shapes];
                      // When extending endpoint, update slope (no longer exact duplicate)
                      newShapes[i] = {
                        ...newShapes[i],
                        points: [
                          pos.x,
                          pos.y,
                          pixelPoints[2],
                          pixelPoints[3],
                        ],
                        dataPoints: newShapes[i].dataPoints ? [
                          newStartData.timestamp,
                          newStartData.price,
                          newShapes[i].dataPoints[2],
                          newShapes[i].dataPoints[3],
                        ] : null,
                        // Clear duplicate status when endpoint is modified
                        isDuplicate: false,
                        originalSlope: null,
                      };
                      setShapes(newShapes);
                    }}
                  />
                  {/* End endpoint circle - visible on hover, hidden when posting */}
                  <Circle
                    x={pixelPoints[2]}
                    y={pixelPoints[3]}
                    radius={showCircles ? 6 : 3}
                    fill="transparent"
                    stroke={showCircles ? "#004C50" : "transparent"}
                    strokeWidth={showCircles ? 2.5 : 0}
                    draggable={canEdit}
                    opacity={showCircles ? 1 : 0}
                    onMouseEnter={(e) => {
                      e.target.getStage().container().style.cursor = 'grab';
                    }}
                    onMouseLeave={(e) => {
                      e.target.getStage().container().style.cursor = 'default';
                    }}
                    onDragStart={(e) => {
                      e.target.getStage().container().style.cursor = 'grabbing';
                    }}
                    onDragEnd={(e) => {
                      e.target.getStage().container().style.cursor = 'grab';
                    }}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      // Update lines imperatively for real-time visual feedback
                      const newPoints = [pixelPoints[0], pixelPoints[1], pos.x, pos.y];
                      const group = e.target.parent;
                      const clipped = clipLineToChart(newPoints, width, height);
                      const displayClipped = shortenLineForCircles(clipped, newPoints, 6);
                      const visualLine = group.findOne('.visual-line');
                      const overlayLine = group.findOne('.overlay-line');
                      if (visualLine) visualLine.points(displayClipped);
                      if (overlayLine) overlayLine.points(clipped);

                      // Convert new pixel position to data coordinates (timestamp)
                      const newEndData = pixelToData(pos.x, pos.y);

                      const newShapes = [...shapes];
                      // When extending endpoint, update slope (no longer exact duplicate)
                      newShapes[i] = {
                        ...newShapes[i],
                        points: [
                          pixelPoints[0],
                          pixelPoints[1],
                          pos.x,
                          pos.y,
                        ],
                        dataPoints: newShapes[i].dataPoints ? [
                          newShapes[i].dataPoints[0],
                          newShapes[i].dataPoints[1],
                          newEndData.timestamp,
                          newEndData.price,
                        ] : null,
                        // Clear duplicate status when endpoint is modified
                        isDuplicate: false,
                        originalSlope: null,
                      };
                      setShapes(newShapes);
                    }}
                  />
                </Group>
              );
            } else {
              // For vertical and horizontal lines, compute pixel positions from data coordinates
              let pixelPoints = shape.points;

              if (shape.type === ST_VERTICAL_LINE && shape.dataPoints && data && data.length > 0) {
                // Vertical line: use timestamp for X, full height for Y
                const pixelData = dataToPixel(shape.dataPoints[0], 0);
                if (pixelData.x !== null) {
                  pixelPoints = [pixelData.x, 0, pixelData.x, height];
                }
              } else if (shape.type === ST_HORIZONTAL_LINE && shape.dataPoints && yScale) {
                // Horizontal line: full width for X, use price for Y
                const y = yScale(shape.dataPoints[1]);
                pixelPoints = [0, y, width, y];
              }

              // Completed vertical/horizontal lines stay draggable in draw mode
              // too, so users can reposition them without switching to SELECT.
              const canEdit = !!shape.isComplete;

              return (
                <Group key={i}>
                  {/* Visual line */}
                  <Line
                    points={pixelPoints}
                    stroke="#004C50"
                    strokeWidth={1}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation={
                      isDraggable ? "source-over" : "destination-over"
                    }
                    // Line itself is not directly draggable.
                    draggable={false}
                  />
                  {/* Draggable transparent overlay to move the whole line */}
                  <Line
                    points={pixelPoints}
                    stroke="black"
                    strokeWidth={10}
                    opacity={0}
                    draggable={canEdit}
                    onDragStart={(e) => {
                      groupDragInfo.current[i] = {
                        initialDataPoints: shape.dataPoints ? shape.dataPoints.slice() : null,
                        initialPoints: pixelPoints.slice(),
                        initialPos: { x: e.target.x(), y: e.target.y() },
                      };
                    }}
                    onDragMove={(e) => {
                      const info = groupDragInfo.current[i];
                      if (info) {
                        const deltaX = e.target.x() - info.initialPos.x;
                        const deltaY = e.target.y() - info.initialPos.y;

                        let newPoints, newDataPoints;

                        if (shape.type === ST_VERTICAL_LINE) {
                          // Vertical line: only move X
                          newPoints = [
                            info.initialPoints[0] + deltaX,
                            info.initialPoints[1],
                            info.initialPoints[2] + deltaX,
                            info.initialPoints[3],
                          ];
                          const newData = pixelToData(newPoints[0], 0);
                          newDataPoints = [newData.timestamp, null, newData.timestamp, null];
                        } else if (shape.type === ST_HORIZONTAL_LINE) {
                          // Horizontal line: only move Y
                          newPoints = [
                            info.initialPoints[0],
                            info.initialPoints[1] + deltaY,
                            info.initialPoints[2],
                            info.initialPoints[3] + deltaY,
                          ];
                          const newData = pixelToData(0, newPoints[1]);
                          newDataPoints = [null, newData.price, null, newData.price];
                        } else {
                          newPoints = [
                            info.initialPoints[0] + deltaX,
                            info.initialPoints[1] + deltaY,
                            info.initialPoints[2] + deltaX,
                            info.initialPoints[3] + deltaY,
                          ];
                          newDataPoints = shape.dataPoints;
                        }

                        const newShapes = [...shapes];
                        newShapes[i] = { ...newShapes[i], points: newPoints, dataPoints: newDataPoints };
                        setShapes(newShapes);
                      }
                    }}
                    onDragEnd={(e) => {
                      // Reset overlay's position after dragging.
                      e.target.position({ x: 0, y: 0 });
                    }}
                  />
                  {/* Start endpoint (smaller circle) */}
                  <Circle
                    x={pixelPoints[0]}
                    y={pixelPoints[1]}
                    radius={3}
                    fill="black"
                    draggable={canEdit}
                    opacity={0}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      const newData = pixelToData(pos.x, pos.y);
                      const newShapes = [...shapes];

                      if (shape.type === ST_VERTICAL_LINE) {
                        newShapes[i] = {
                          ...newShapes[i],
                          points: [pos.x, 0, pos.x, height],
                          dataPoints: [newData.barIndex, null, newData.barIndex, null],
                        };
                      } else if (shape.type === ST_HORIZONTAL_LINE) {
                        newShapes[i] = {
                          ...newShapes[i],
                          points: [0, pos.y, width, pos.y],
                          dataPoints: [null, newData.price, null, newData.price],
                        };
                      } else {
                        newShapes[i] = {
                          ...newShapes[i],
                          points: [
                            pos.x,
                            pos.y,
                            newShapes[i].points[2],
                            newShapes[i].points[3],
                          ],
                        };
                      }
                      setShapes(newShapes);
                    }}
                  />
                  {/* End endpoint (smaller circle) */}
                  <Circle
                    x={pixelPoints[2]}
                    y={pixelPoints[3]}
                    radius={3}
                    fill="black"
                    draggable={canEdit}
                    opacity={0}
                    onDragMove={(e) => {
                      const pos = e.target.position();
                      const newData = pixelToData(pos.x, pos.y);
                      const newShapes = [...shapes];

                      if (shape.type === ST_VERTICAL_LINE) {
                        newShapes[i] = {
                          ...newShapes[i],
                          points: [pos.x, 0, pos.x, height],
                          dataPoints: [newData.barIndex, null, newData.barIndex, null],
                        };
                      } else if (shape.type === ST_HORIZONTAL_LINE) {
                        newShapes[i] = {
                          ...newShapes[i],
                          points: [0, pos.y, width, pos.y],
                          dataPoints: [null, newData.price, null, newData.price],
                        };
                      } else {
                        newShapes[i] = {
                          ...newShapes[i],
                          points: [
                            newShapes[i].points[0],
                            newShapes[i].points[1],
                            pos.x,
                            pos.y,
                          ],
                        };
                      }
                      setShapes(newShapes);
                    }}
                  />
                </Group>
              );
            }
          })}
        </Layer>
      </Stage>
    </>
  );
};

export default AnnotationStage;
